import { Stack } from '@/components/layout';
import { Button, Collapsible } from '@/components/ui';
import { Select } from '@/components/ui/select';
import Text from '@/components/ui/text/text';
import { STRINGS } from '@/constants/strings';
import { useMutateInactiveFilterDefault } from '@/features/coach/queries';
import { useCoachQuery } from '@/features/home/queries';
import { homeKeys } from '@/features/home/queries/query-keys';
import { PlayerLastActiveFilter } from '@/features/player/context/player-filters-context';
import { LAST_ACTIVE_OPTIONS } from '@/features/player/utils/helpers';
import { createStyles, useScaling } from '@/hooks/use-scaling';
import { captureException } from '@/libs/logger';
import { useSessionStore } from '@/stores/session-store';
import { useUiStore } from '@/stores/ui-store';
import { colors } from '@/theme/colors';
import { getInactiveKeyFromNumber } from '@/utils/helper';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Platform } from 'react-native';
import { Picker } from 'react-native-wheel-pick';
import { useShallow } from 'zustand/react/shallow';

const lastActiveOptions: Array<{ value: PlayerLastActiveFilter; label: string }> = [
  { value: 'show_all', label: STRINGS.player.filters.lastActive.noInactiveFilter },
  { value: '1_month', label: STRINGS.player.filters.lastActive.oneMonth },
  { value: '3_months', label: STRINGS.player.filters.lastActive.threeMonths },
  { value: '6_months', label: STRINGS.player.filters.lastActive.sixMonths },
  { value: '1_5_years', label: STRINGS.player.filters.lastActive.oneHalfYears },
];

// Button config inactive filter
export function ButtonConfigInactiveFilter() {
  const { scale } = useScaling();
  const [open, setOpen] = useState(false);
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));
  const queryClient = useQueryClient();
  const styles = useStyles();
  const uid = useSessionStore(useShallow(state => state.user?.uid));
  const { data: coach } = useCoachQuery(uid);

  const [inactiveFilterLevel, setInactiveFilterLevel] = useState<PlayerLastActiveFilter>(
    getInactiveKeyFromNumber(coach?.inactiveFilterLevel ?? 0) ?? 'show_all',
  );

  const selectedValue =
    lastActiveOptions.find(option => option.value === inactiveFilterLevel)?.value ?? 'show_all';

  const { mutateAsync: setInactiveFilterDefault } = useMutateInactiveFilterDefault();

  const onButtonPress = async () => {
    try {
      if (open) {
        showGlobalLoading();
        await setInactiveFilterDefault({
          coachId: coach?.id ?? '',
          inactiveFilterLevel:
            LAST_ACTIVE_OPTIONS.find(option => option.text === inactiveFilterLevel)?.seconds ?? 0,
        });
        queryClient.invalidateQueries({ queryKey: homeKeys.coach(uid) });
      }
      setOpen(prev => !prev);
    } catch (error) {
      captureException(
        {
          error_payload: error,
        },
        { tags: { usk_log_type: 'button_config_inactive_filter_error' } },
      );
    } finally {
      hideGlobalLoading();
    }
  };

  return (
    <Stack align="center">
      <Text
        variant="Font Inter/Subtitle/Regular 11px"
        fontSize={scale(10)}
        style={{ color: colors['6'] }}>
        {STRINGS.coachProfile.content.configInactiveFilter}
      </Text>
      <Text
        fontFamily="Montserrat"
        fontSize={scale(14)}
        fontWeight="bold"
        lineHeight={scale(14 * 1.3)}
        style={styles.inactiveFilterText}>
        {lastActiveOptions.find(option => option.value === selectedValue)?.label ?? ''}
      </Text>
      <Collapsible
        open={open}
        containerStyle={[styles.pickerContainerStyle, open ? styles.pickerContainer : {}]}>
        <Collapsible.Content>
          <Stack justify="center" zIndex={999} align="center">
            {Platform.OS === 'web' ? (
              <Stack style={styles.webSelectWrap}>
                <Select
                  options={lastActiveOptions}
                  value={selectedValue}
                  onValueChange={value => {
                    if (value) setInactiveFilterLevel(value as PlayerLastActiveFilter);
                  }}
                  placeholder={STRINGS.common.selectDefaultPlaceholder}
                  closeOnSelect>
                  <Select.Trigger style={styles.pickerStyle} hideIcon={false} />
                  <Select.Content maxHeight={scale(360)} />
                </Select>
              </Stack>
            ) : (
              <Picker
                style={styles.pickerStyle}
                selectedValue={selectedValue}
                pickerData={lastActiveOptions}
                onValueChange={(value: PlayerLastActiveFilter) => {
                  if (value) {
                    setInactiveFilterLevel(value);
                  }
                }}
              />
            )}
          </Stack>
        </Collapsible.Content>
        <Collapsible.Trigger triggerStyle={styles.buttonTrigger} hideDefaultIcon>
          <Button onPress={onButtonPress}>
            <Button.Text>
              {open
                ? STRINGS.coachProfile.content.done
                : STRINGS.coachProfile.content.updateDefaultInactiveFilter}
            </Button.Text>
          </Button>
        </Collapsible.Trigger>
      </Collapsible>
    </Stack>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  // Button config inactive filter
  buttonTrigger: { width: scale({ phone: 305, tablet: 323 }), alignSelf: 'center' },
  webSelectWrap: {
    width: scale({ phone: 305, tablet: 323 }),
    alignSelf: 'center',
    marginBottom: scale(8),
  },
  pickerStyle: { width: scale({ phone: 305, tablet: 323 }), backgroundColor: 'white' },
  pickerContainerStyle: { gap: 0, paddingBottom: scale(16) },
  pickerContainer: {
    width: isTablet ? scale(375) : '100%',
    borderRadius: 4,
    backgroundColor: 'white',
    overflow: 'visible',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
        backgroundColor: 'white',
      },
      android: {
        elevation: 2,
        paddingTop: scale(8),
      },
    }),
  },
  inactiveFilterText: {
    color: colors.Blue,
    marginBottom: scale(8),
    marginTop: scale(4),
    width: '100%',
    textAlign: 'center',
  },
}));
